import { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, AlertTriangle, Check, Sparkles, Search, ArrowRight, Wine, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOutletContext } from 'react-router-dom';
import {
  getSemanticSearchStatus,
  updateSemanticSearchConfig,
  triggerEmbeddingReindex,
  getAIContextConfig,
  updateAIContextConfig,
  clearAIContextCache,
} from '../../utils/api';
import type { SemanticSearchStatus, AIContextConfig } from '../../utils/types';

interface AdminContext {
  secret: string;
}

export default function AdminAISearch() {
  const { secret } = useOutletContext<AdminContext>();
  const [status, setStatus] = useState<SemanticSearchStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Semantic search form state
  const [enabled, setEnabled] = useState(false);
  const [vectorWeight, setVectorWeight] = useState(0.3);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.5);
  const [minWordCount, setMinWordCount] = useState(3);

  // AI Context form state
  const [aiConfig, setAiConfig] = useState<AIContextConfig | null>(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiTone, setAiTone] = useState('sommelier');
  const [aiResponseStyle, setAiResponseStyle] = useState('detailed');
  const [aiModel, setAiModel] = useState('gpt-4o-mini');
  const [aiMaxTokens, setAiMaxTokens] = useState(500);
  const [aiCacheTTL, setAiCacheTTL] = useState(7);
  const [aiSavingContext, setAiSavingContext] = useState(false);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const load = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      getSemanticSearchStatus(secret),
      getAIContextConfig(secret).catch(() => null),
    ])
      .then(([s, ai]) => {
        setStatus(s);
        setEnabled(s.enabled);
        setVectorWeight(s.vectorWeight);
        setSimilarityThreshold(s.similarityThreshold);
        setMinWordCount(s.minWordCount);
        setIsReindexing(s.isReindexing);

        if (ai) {
          setAiConfig(ai);
          setAiEnabled(ai.enabled);
          setAiTone(ai.tone ?? 'sommelier');
          setAiResponseStyle(ai.responseStyle ?? 'detailed');
          setAiModel(ai.model ?? 'gpt-4o-mini');
          setAiMaxTokens(ai.maxTokens ?? 500);
          setAiCacheTTL(ai.cacheTTLDays ?? 7);
        }
      })
      .catch((e) => showMsg('error', e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setIsLoading(false));
  }, [secret]);

  useEffect(() => { load(); }, [load]);

  // Poll for reindex status while reindexing
  useEffect(() => {
    if (!isReindexing) return;
    const interval = setInterval(() => {
      getSemanticSearchStatus(secret)
        .then((s) => {
          setStatus(s);
          if (!s.isReindexing) {
            setIsReindexing(false);
            showMsg('success', `Reindex complete! ${s.indexedCount} products indexed.`);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(interval);
  }, [isReindexing, secret]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSemanticSearchConfig(secret, {
        enabled,
        vectorWeight,
        similarityThreshold,
        minWordCount,
      });
      load();
      showMsg('success', 'Configuration saved!');
    } catch (e) {
      showMsg('error', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAIContext = async () => {
    setAiSavingContext(true);
    try {
      await updateAIContextConfig(secret, {
        enabled: aiEnabled,
        tone: aiTone,
        responseStyle: aiResponseStyle,
        model: aiModel,
        maxTokens: aiMaxTokens,
        cacheTTLDays: aiCacheTTL,
      });
      load();
      showMsg('success', 'AI Context configuration saved!');
    } catch (e) {
      showMsg('error', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setAiSavingContext(false);
    }
  };

  const handleClearCache = async () => {
    try {
      const result = await clearAIContextCache(secret);
      showMsg('success', `AI context cache cleared (${result.deleted} entries removed)`);
    } catch (e) {
      showMsg('error', e instanceof Error ? e.message : 'Failed to clear cache');
    }
  };

  const handleReindex = async () => {
    try {
      setIsReindexing(true);
      const result = await triggerEmbeddingReindex(secret);
      showMsg('success', `Reindex started for ${result.totalProducts} products...`);
    } catch (e) {
      setIsReindexing(false);
      showMsg('error', e instanceof Error ? e.message : 'Reindex failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!status) return null;

  const hasSemanticChanges =
    enabled !== status.enabled ||
    vectorWeight !== status.vectorWeight ||
    similarityThreshold !== status.similarityThreshold ||
    minWordCount !== status.minWordCount;

  const hasAIContextChanges = aiConfig != null && (
    aiEnabled !== aiConfig.enabled ||
    aiTone !== (aiConfig.tone ?? 'sommelier') ||
    aiResponseStyle !== (aiConfig.responseStyle ?? 'detailed') ||
    aiModel !== (aiConfig.model ?? 'gpt-4o-mini') ||
    aiMaxTokens !== (aiConfig.maxTokens ?? 500) ||
    aiCacheTTL !== (aiConfig.cacheTTLDays ?? 7)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-6 w-6" />
            AI Semantic Search
          </h1>
          <p className="text-muted-foreground">
            Hybrid keyword + vector search powered by OpenAI embeddings
          </p>
        </div>
        <div className="flex items-center gap-2">
          {message && (
            <div className={`text-sm px-3 py-1.5 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400'}`}>
              {message.text}
            </div>
          )}
        </div>
      </div>

      {/* API Key Warning */}
      {!status.apiKeyConfigured && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
          <CardContent className="flex items-start gap-3 pt-4">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">OpenAI API Key Not Configured</p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Set <code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">OPENAI_API_KEY</code> in your backend environment variables to enable semantic search.
                Without it, search will use keyword-only mode.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Index Status</CardTitle>
            <CardDescription>Current state of the embedding index</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">API Key</Label>
                <div className="mt-1">
                  <Badge variant={status.apiKeyConfigured ? 'default' : 'destructive'}>
                    {status.apiKeyConfigured ? 'Configured' : 'Missing'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Model</Label>
                <p className="text-sm font-mono mt-1">{status.model}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Indexed Products</Label>
                <p className="text-sm font-medium mt-1">
                  {status.indexedCount} / {status.totalProducts}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Last Reindex</Label>
                <p className="text-sm mt-1">
                  {status.lastReindexAt
                    ? new Date(status.lastReindexAt).toLocaleString('el-GR')
                    : 'Never'}
                </p>
              </div>
            </div>

            <Separator />

            <Button
              onClick={handleReindex}
              disabled={!status.apiKeyConfigured || isReindexing}
              variant="outline"
              className="w-full"
            >
              {isReindexing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Reindexing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Reindex Embeddings
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Semantic Search Configuration Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base">Vector Search</CardTitle>
              <CardDescription>Tune hybrid search behavior</CardDescription>
            </div>
            <Button onClick={handleSave} disabled={isSaving || !hasSemanticChanges} size="sm">
              {isSaving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
              Save
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="ai-enabled" className="font-medium">Enable Semantic Search</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Activate vector search for intent-rich queries
                </p>
              </div>
              <Switch
                id="ai-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
                disabled={!status.apiKeyConfigured}
              />
            </div>

            <Separator />

            {/* Vector Weight Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Vector Weight</Label>
                <span className="text-sm font-mono text-muted-foreground">{vectorWeight.toFixed(2)}</span>
              </div>
              <Slider
                value={[vectorWeight]}
                onValueChange={([v]) => setVectorWeight(v)}
                min={0}
                max={1}
                step={0.05}
                disabled={!enabled}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Keyword Heavy</span>
                <span>Balanced</span>
                <span>Vector Heavy</span>
              </div>
            </div>

            {/* Similarity Threshold Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Similarity Threshold</Label>
                <span className="text-sm font-mono text-muted-foreground">{similarityThreshold.toFixed(2)}</span>
              </div>
              <Slider
                value={[similarityThreshold]}
                onValueChange={([v]) => setSimilarityThreshold(v)}
                min={0}
                max={1}
                step={0.05}
                disabled={!enabled}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>More Results</span>
                <span>More Precise</span>
              </div>
            </div>

            {/* Min Word Count */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="min-words" className="font-medium">Min Word Count</Label>
              </div>
              <Input
                id="min-words"
                type="number"
                min={1}
                max={10}
                value={minWordCount}
                onChange={(e) => setMinWordCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
                disabled={!enabled}
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">
                Queries with fewer words use keyword-only search (unless a contextual intent is detected).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Context (Sommelier) Configuration */}
      {aiConfig && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Wine className="h-4 w-4" />
                AI Sommelier Context
              </CardTitle>
              <CardDescription>
                Configure AI-powered wine recommendations and pairing advice shown in search results
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleClearCache} variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-1" />
                Clear Cache
              </Button>
              <Button onClick={handleSaveAIContext} disabled={aiSavingContext || !hasAIContextChanges} size="sm">
                {aiSavingContext ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="ai-context-enabled" className="font-medium">Enable AI Context</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Show sommelier-style wine recommendations for pairing and occasion queries
                </p>
              </div>
              <Switch
                id="ai-context-enabled"
                checked={aiEnabled}
                onCheckedChange={setAiEnabled}
                disabled={!status.apiKeyConfigured}
              />
            </div>

            <Separator />

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Tone */}
              <div className="space-y-2">
                <Label htmlFor="ai-tone" className="font-medium">Tone</Label>
                <Select value={aiTone} onValueChange={setAiTone} disabled={!aiEnabled}>
                  <SelectTrigger id="ai-tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sommelier">Sommelier (Professional)</SelectItem>
                    <SelectItem value="friendly">Friendly (Conversational)</SelectItem>
                    <SelectItem value="educational">Educational (Explain why)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls the style of AI responses shown to users
                </p>
              </div>

              {/* Response Style */}
              <div className="space-y-2">
                <Label htmlFor="ai-style" className="font-medium">Response Style</Label>
                <Select value={aiResponseStyle} onValueChange={setAiResponseStyle} disabled={!aiEnabled}>
                  <SelectTrigger id="ai-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="detailed">Detailed (2-3 wine suggestions with reasoning)</SelectItem>
                    <SelectItem value="brief">Brief (1-2 sentences, concise)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  How much detail to include in wine recommendations
                </p>
              </div>

              {/* Model */}
              <div className="space-y-2">
                <Label htmlFor="ai-model" className="font-medium">AI Model</Label>
                <Select value={aiModel} onValueChange={setAiModel} disabled={!aiEnabled}>
                  <SelectTrigger id="ai-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast, low cost)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o (Higher quality)</SelectItem>
                    <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
                    <SelectItem value="gpt-4.1-nano">GPT-4.1 Nano (Fastest)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  OpenAI model for generating wine recommendations
                </p>
              </div>

              {/* Max Tokens */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ai-tokens" className="font-medium">Max Tokens</Label>
                  <span className="text-sm font-mono text-muted-foreground">{aiMaxTokens}</span>
                </div>
                <Slider
                  value={[aiMaxTokens]}
                  onValueChange={([v]) => setAiMaxTokens(v)}
                  min={100}
                  max={1000}
                  step={50}
                  disabled={!aiEnabled}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Shorter</span>
                  <span>Longer</span>
                </div>
              </div>

              {/* Cache TTL */}
              <div className="space-y-2">
                <Label htmlFor="ai-cache" className="font-medium">Cache Duration (days)</Label>
                <Input
                  id="ai-cache"
                  type="number"
                  min={1}
                  max={90}
                  value={aiCacheTTL}
                  onChange={(e) => setAiCacheTTL(Math.max(1, Math.min(90, parseInt(e.target.value) || 7)))}
                  disabled={!aiEnabled}
                  className="w-24"
                />
                <p className="text-xs text-muted-foreground">
                  How long to cache AI responses before regenerating
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            How It Works
          </CardTitle>
          <CardDescription>
            Vector search activates selectively based on query type. Simple lookups stay fast; complex queries get smarter results.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { query: 'cabernet', mode: 'keyword', reason: 'Single word, below min word count' },
              { query: 'merlot 2019', mode: 'keyword', reason: '2 words, no contextual intent' },
              { query: 'wine for fish', mode: 'hybrid', reason: 'Pairing intent detected' },
              { query: 'gift wine anniversary', mode: 'hybrid', reason: 'Occasion intent detected' },
              { query: 'white wine for seafood under 15', mode: 'hybrid', reason: '6 words + price intent' },
              { query: 'light summer red wine', mode: 'hybrid', reason: '4 words, meets min word count' },
            ].map((example) => (
              <div
                key={example.query}
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
              >
                <Badge
                  variant={example.mode === 'hybrid' ? 'default' : 'secondary'}
                  className="shrink-0 mt-0.5 text-xs"
                >
                  {example.mode === 'hybrid' ? 'Hybrid' : 'Keyword'}
                </Badge>
                <div className="min-w-0">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <span className="truncate">"{example.query}"</span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground shrink-0">
                      {example.mode === 'hybrid' ? 'AI + Keywords' : 'Keywords only'}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{example.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
